<template>
  <main class="wrapper-content position-relative w-100">
    <LmLoading v-if="isLoading" />
    <template v-else>
      <h1>Generador de Bandejas de Entradas</h1>
      <b-row>
        <LmTextInput
          label="Nombre de la Bandeja de Entradas"
          placeholder="Nombre de la Bandeja de Entradas"
          class="col-12"
          v-model="inboxSettings.name"
          :v="$v.inboxSettings.name"
        />
      </b-row>
      <h4 class="mb-4">Configuración de la Bandeja de Entrada</h4>
      <b-row>
        <b-col cols="12" md="6">
          <b-row class="mb-4">
            <LmTreeSelect
              class="col-12"
              label="Formato"
              :options="widgetFormatOptions"
              v-model="widgetFormat"
              @input="newWidget = null"
            />
            <LmTextInput
              class="col-12"
              label="Título del grupo"
              placeholder="Escribe un título para el grupo"
              v-model="widgetGroupLabel"
              v-if="widgetFormat.id === 'group'"
            />
            <LmTreeSelect
              class="col-12"
              label="Widgets"
              :options="widgetsTreeSelectOptions"
              v-model="newWidget"
              :multiple="widgetFormat.id === 'group'"
            />
            <LmButton
              class="col-12"
              :disabled="disableAddWidgetButton"
              type="button"
              size="sm"
              buttonClass="btn-primary"
              :value="$t('add')"
              @click="onAddWidget"
            />
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <section class="col-6">
          <draggable
            v-model="inboxWidgets"
            @start="drag=true"
            @end="drag=false"
            animation="150"
            handle=".handle"
            >
            <div v-for="(widget, index) in inboxWidgets" :key="index" class="mb-4">
              <header
                class="d-flex justify-content-between align-items-center shadow rounded-lg text-white bg-primary px-3 handle"
              >
                <font-awesome-icon class="mr-4" icon="arrows-alt" style="width:18px; height:18px;" />
                <h4 v-b-toggle="`collapse-${index}`" class="flex-grow-1 text-center my-0 py-3 handle">
                  {{ widgetName(widget) }}
                  <b-badge v-if="isWidgetGroup(widget)" variant="light">Grupo</b-badge>
                </h4>
                <div class="d-flex align-items-center">
                  <font-awesome-icon v-b-toggle="`collapse-${index}`" class="ml-4 clickable" icon="chevron-down" style="width:16px; height:16px;" />
                  <font-awesome-icon
                    v-if="inboxWidgets.length > 1"
                    class="ml-4 clickable"
                    icon="trash"
                    style="width:16px; height:16px;"
                    @click="onRemoveWidget(index)"
                  />
                </div>
              </header>
              <b-collapse
                accordion="my-accordion"
                :id="`collapse-${index}`"
                visible
                class="p-4 border border-top-0 rounded-lg"
                >
                <b-row>
                  <h4 class="col-12 mb-4">Configuración del widget</h4>
                  <template v-if="isWidgetGroup(widget)">
                    <b-col cols="12">
                      <b-row
                        v-for="(item, index) in widget.render"
                        :key="index"
                      >
                        <h6 class="col-12">{{ item.name }}</h6>
                        <LmSelect
                          class="col-4"
                          label="Tamaño"
                          v-model="item.customization.size"
                          :options="sizeOptions(item)"
                        />
                        <LmTextInput
                          class="col-4"
                          :label="$t('title')"
                          :placeholder="$t('title')"
                          v-model="item.customization.title"
                        />
                        <LmColorPicker
                          class="col-4"
                          label="Color de fondo"
                          :swatches="swatches"
                          v-model="item.customization.color"
                        />
                        <hr class="w-100" />
                      </b-row>
                    </b-col>
                  </template>
                  <template v-else>
                    <LmSelect
                      class="col-4"
                      label="Tamaño"
                      v-model="widget.render.customization.size"
                      :options="sizeOptions(widget.render)"
                    />
                    <LmTextInput
                      class="col-4"
                      :label="$t('title')"
                      :placeholder="$t('title')"
                      v-model="widget.render.customization.title"
                    />
                    <LmColorPicker
                      class="col-4"
                      label="Color de fondo"
                      :swatches="swatches"
                      v-model="widget.render.customization.color"
                    />
                  </template>
                </b-row>
              </b-collapse>
            </div>
          </draggable>
        </section>
        <section class="col-6">
          <draggable
            class="row"
            v-model="inboxWidgets"
            @start="drag=true"
            @end="drag=false"
            animation="150"
            handle=".handle"
          >
            <b-col
              class="handle"
              v-for="(widget, index) in inboxWidgets" :key="index"
              :cols="!isWidgetGroup(widget) ? widget.render.customization.size : 12"
              role="grid"
            >
              <b-card :style="`background-color: ${isWidgetGroup(widget) ? 'white' : widget.render.customization.color}`" v-b-toggle="`collapse-${index}`" role="gridcell">
                <h4>
                  <template v-if="isWidgetGroup(widget)">
                    <b-badge
                      v-for="(item, index) in widget.render"
                      :key="index"
                      class="mr-1"
                      variant="light"
                      :style="`background-color: ${item.customization.color}`"
                    >
                      {{ item.customization.title || item.name }}
                    </b-badge>
                  </template>
                  <template v-else>
                    <b-badge variant="light">{{ widgetName(widget) }}</b-badge>
                  </template>
                </h4>
              </b-card>
            </b-col>
          </draggable>
        </section>
      </b-row>
      <hr class="w-100">
      <b-row>
        <b-col cols="6">
          <h4 class="mb-4">¿Quiénes verán esta Bandeja de Entradas?</h4>
          <b-row>
            <LmTreeSelect
              label="Áreas"
              class="col-6"
              multiple
              pathToGetOptions="/customer/areas"
              v-model="inboxSettings.areas"
              placeholder="Seleccione las áreas que tendrán acceso"
            />
            <LmTreeSelect
              label="SubÁreas"
              class="col-6"
              multiple
              pathToGetOptions="/customer/subareas"
              v-model="inboxSettings.subareas"
              placeholder="Seleccione las subáreas que tendrán acceso"
            />
            <LmTreeSelect
              label="Roles"
              class="col-12"
              multiple
              pathToGetOptions="/customer/roles"
              v-model="inboxSettings.roles"
              placeholder="Seleccione los roles que tendrán acceso"
            />
          </b-row>
        </b-col>
      </b-row>
      <b-row class="mt-4">
        <LmButton
          buttonClass="btn-secondary"
          class="col-auto"
          :value="$t('cancel')"
          action="cancel"
          @click="$router.push(pathToRedirectBack)"
        />
        <LmButton
          v-if="!inboxSettings.deleted"
          :disabled="!inboxWidgets.length"
          buttonClass="btn-primary"
          class="col-auto"
          :value="$t('save')"
          action="submit"
          @click="onSubmit"
        />
        <LmButton
          v-if="isOnEditMode && !inboxSettings.deleted"
          buttonClass="btn-danger"
          class="col-auto"
          value="Dar de baja"
          action="delete"
          @click="onDelete"
        />
        <LmButton
          v-if="isOnEditMode && inboxSettings.deleted"
          class="col-auto"
          value="Dar de alta"
          action="submit"
          @click="onActivate"
        />
        <p class="col-12 text-danger" v-if="!inboxWidgets.length">Agregar al menos un widget a la bandeja de entradas para guardarla.</p>
      </b-row>
    </template>
  </main>
</template>

<script>
// Components
import LmSelect from '@/components/form/elements/LmSelect'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmColorPicker from '@/components/form/elements/LmColorPicker'

// Utils
import draggable from 'vuedraggable'
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'
import { required } from 'vuelidate/lib/validators'

export default {
  mixins: [lmToasts],
  components: {
    draggable,
    LmSelect,
    LmButton,
    LmTextInput,
    LmColorPicker,
    LmTreeSelect
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    pathToRedirectBack: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      isLoading: false,
      inboxSettings: {
        name: '',
        roles: [],
        areas: [],
        subareas: [],
        deleted: false
      },
      widgetFormat: { id: 'single', name: 'Individual' },
      widgetGroupLabel: '',
      newWidget: null,
      // El inbox con la estructura deseada
      inboxWidgets: [],
      // Necesito los widgets
      widgets: [],
      swatches: [
        ['#F6648B', '#F493A7', '#F891A6', '#FFCCD5'],
        ['#a27bff', '#b99cff', '#d0bdff', '#e8deff'],
        ['#74ebe3', '#96f0ea', '#b9f5f1', '#dcfaf8'],
        ['#ffb748', '#ffc976', '#ffdba3', '#ffedd1'],
        ['']
      ],
      sizes: {
        3: '25%',
        6: '50%',
        9: '75%',
        12: '100%'
      }
    }
  },
  validations: {
    inboxSettings: {
      name: {
        required
      }
    }
  },
  computed: {
    disableAddWidgetButton () {
      if (this.widgetFormat.id === 'single' && this.newWidget) {
        return false
      }
      if (this.widgetFormat.id === 'group' && this.widgetGroupLabel && this.newWidget.length) {
        return false
      }
      return true
    },
    widgetFormatOptions () {
      return [
        { id: 'single', name: 'Individual' },
        { id: 'group', name: 'Grupo' }
      ]
    },
    widgetsTreeSelectOptions () {
      return this.widgets.map(el => {
        return {
          id: el.id,
          name: el.name
        }
      })
    }
  },
  methods: {
    isWidgetGroup (widget) {
      return widget.type === 'group'
    },
    widgetName (widget) {
      let name = ''
      if (widget.type === 'group') {
        name = widget.label || widget.render.map(el => el.name).join(', ')
      } else {
        name = widget.render.name
      }
      return name
    },
    sizeOptions ({ properties }) {
      const options = properties.sizes.map(size => {
        return {
          value: size,
          text: this.sizes[size]
        }
      })
      return {
        content: options
      }
    },
    showTitleInput (widget) {
      return this.widgets.find(w => w.id === widget.widgetId).component === 'LmList'
    },
    onWidgetIsSelected (event, index) {
      const widgetData = this.widgets.find(el => el.id === event)
      for (const key in this.inboxWidgets.at(index)) {
        this.inboxWidgets.at(index)[key] = widgetData[key]
      }
    },
    mapWidgetProperties (event) {
      const widget = this.widgets.find(el => el.id === event.id)
      return {
        ...widget,
        customization: {
          size: Math.max.apply(null, widget?.properties?.sizes) || 12,
          color: ''
        }
      }
    },
    onAddWidget () {
      const widget = {
        label: this.widgetGroupLabel || null,
        type: this.widgetFormat.id,
        render: null
      }
      if (this.widgetFormat.id === 'group') {
        widget.render = this.newWidget.map(el => {
          return this.mapWidgetProperties(el)
        })
      } else {
        widget.render = this.mapWidgetProperties(this.newWidget)
      }

      this.inboxWidgets.push(widget)
      this.newWidget = null
      this.widgetGroupLabel = ''
    },
    onRemoveWidget (index) {
      this.inboxWidgets.splice(index, 1)
    },
    async onSubmit () {
      try {
        this.isLoading = true
        await this.validateForm(this.$v)
        if (!this.inboxWidgets.length) {
          throw new Error('Debes cargar al menos un widget en la bandeja de entradas.')
        }
        const dataToSendToServer = {
          widgets: this.inboxWidgets,
          ...this.inboxSettings
        }
        if (this.isOnEditMode) {
          // Edición
          const res = await commonsApi.axiosPutWrapper(`/inbox/${this.$route.params.id}`, dataToSendToServer)
          this.lmToastSuccessMessage(`Se editó ${res?.data?.name || ' la bandeja de entrada'} correctamente.`, 'Se guardó exitosamente')
        } else {
          // Nuevo
          const res = await commonsApi.axiosPostWrapper('/inbox', dataToSendToServer)
          this.lmToastSuccessMessage(`Se creó ${res?.data?.name || ' la bandeja de entrada'} correctamente.`, 'Se guardó exitosamente')
        }
        setTimeout(() => {
          this.$router.push(this.pathToRedirectBack)
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onDelete () {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosPutWrapper(`/inbox/delete/${this.$route.params.id}`)
        this.lmToastSuccessMessage(`Se dió de baja ${res?.data?.name || ' la bandeja de entrada'} correctamente.`, 'Se dió de baja exitosamente')
        setTimeout(() => {
          this.$router.push(this.pathToRedirectBack)
        }, 650)
      } catch (error) {
        console.log(error)
        this.lmToastDangerMessage(error.message, 'Error')
      } finally {
        this.isLoading = false
      }
    },
    async onActivate () {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosPutWrapper(`/inbox/activate/${this.$route.params.id}`)
        this.lmToastSuccessMessage(`Se dió de alta ${res?.data?.name || ' la bandeja de entrada'} correctamente.`, 'Se dió de alta exitosamente')
        setTimeout(() => {
          this.$router.push(this.pathToRedirectBack)
        }, 650)
      } catch (error) {
        console.log(error)
        this.lmToastDangerMessage(error.message, 'Error')
      } finally {
        this.isLoading = false
      }
    },
    validateForm (validation) {
      validation.$touch()
      if (validation.$error) {
        this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        throw new Error('validation error')
      } else {
        if (validation.$pending || validation.$error) {
          throw new Error('validation error')
        }
      }
    }
  },
  async created () {
    try {
      this.isLoading = true
      // Obtengo todos los widgets
      const widgets = await commonsApi.axiosGetWrapper('/widgets/all?view=inbox')
      this.widgets = widgets.data.map(el => {
        return {
          id: el?._id,
          component: el?.component,
          name: el?.name,
          properties: el?.properties
        }
      })
      // Modo Edición
      if (this.isOnEditMode) {
        const inbox = await commonsApi.axiosGetWrapper(`/inbox/${this.$route.params.id}`)
        this.inboxWidgets = inbox?.data?.widgets
        for (const key in this.inboxSettings) {
          if (inbox.data.hasOwnProperty(key)) {
            this.inboxSettings[key] = inbox.data[key]
          }
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  }
}
</script>
